import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircleIcon, CheckCircleIcon, ChevronLeftIcon, ShieldCheckIcon, AlertTriangleIcon, CalendarIcon, GlobeIcon, ServerIcon, UserIcon } from 'lucide-react';
interface DomainDetailsProps {
  brandId: number;
}
export const DomainDetails: React.FC<DomainDetailsProps> = ({
  brandId
}) => {
  const {
    domainId
  } = useParams<{
    domainId: string;
  }>();
  const navigate = useNavigate();
  // Mock domain data
  const domain = {
    id: parseInt(domainId || '1'),
    domain: 'acme-inc.net',
    riskLevel: 'high',
    status: 'active',
    dateFound: '2023-06-15',
    registrar: 'NameCheap, Inc.',
    creationDate: '2023-06-01',
    expiryDate: '2024-06-01',
    lastUpdated: '2023-06-15',
    nameservers: ['ns1.namecheap.com', 'ns2.namecheap.com'],
    whoisPrivacy: true,
    registrantCountry: 'PA',
    similarityScore: 89
  };
  return <div className="space-y-6" data-id="element-44">
      <button onClick={() => navigate(`/brand/${brandId}/domains`)} className="flex items-center text-sm text-gray-600 hover:text-gray-900" data-id="element-45">
        <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-46" />
        Back to Domains
      </button>
      <div className="bg-white shadow-sm rounded-lg" data-id="element-47">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-48">
          <div className="flex items-center justify-between" data-id="element-49">
            <h1 className="text-xl font-semibold text-gray-900" data-id="element-50">
              {domain.domain}
            </h1>
            <div className="flex space-x-3" data-id="element-51">
              <button className="px-4 py-2 bg-green-50 text-green-700 rounded-md hover:bg-green-100 flex items-center text-sm" data-id="element-52">
                <ShieldCheckIcon className="h-4 w-4 mr-2" data-id="element-53" />
                Add to Safe List
              </button>
              <button className="px-4 py-2 bg-red-50 text-red-700 rounded-md hover:bg-red-100 flex items-center text-sm" data-id="element-54">
                <AlertTriangleIcon className="h-4 w-4 mr-2" data-id="element-55" />
                Submit Abuse Report
              </button>
            </div>
          </div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-56">
          <div className="space-y-6" data-id="element-57">
            <div data-id="element-58">
              <h2 className="text-sm font-medium text-gray-500 mb-4" data-id="element-59">
                Domain Status
              </h2>
              <div className="flex space-x-4" data-id="element-60">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                  ${domain.status === 'active' ? 'bg-blue-100 text-blue-800' : domain.status === 'blocked' ? 'bg-gray-100 text-gray-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-61">
                  {domain.status.charAt(0).toUpperCase() + domain.status.slice(1)}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                  ${domain.riskLevel === 'high' ? 'bg-red-100 text-red-800' : domain.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-62">
                  {domain.riskLevel === 'high' ? <AlertCircleIcon className="mr-1 h-4 w-4" data-id="element-63" /> : domain.riskLevel === 'medium' ? <AlertCircleIcon className="mr-1 h-4 w-4" data-id="element-64" /> : <CheckCircleIcon className="mr-1 h-4 w-4" data-id="element-65" />}
                  {domain.riskLevel.charAt(0).toUpperCase() + domain.riskLevel.slice(1)}{' '}
                  Risk
                </span>
              </div>
            </div>
            <div data-id="element-66">
              <h2 className="text-sm font-medium text-gray-500 mb-4" data-id="element-67">
                Threat Analysis
              </h2>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-68">
                <div className="flex items-start" data-id="element-69">
                  <AlertCircleIcon className="h-5 w-5 text-red-500 mr-3 mt-0.5" data-id="element-70" />
                  <div data-id="element-71">
                    <p className="text-sm font-medium text-gray-900" data-id="element-72">
                      Similarity Score
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-73">
                      {domain.similarityScore}% similar to your domain
                    </p>
                    <p className="text-sm text-red-600 mt-2" data-id="element-74">
                      This domain shows strong indication of typosquatting
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-6" data-id="element-75">
            <div data-id="element-76">
              <h2 className="text-sm font-medium text-gray-500 mb-4" data-id="element-77">
                Key Information
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-78">
                <div className="flex items-start" data-id="element-79">
                  <GlobeIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" data-id="element-80" />
                  <div data-id="element-81">
                    <p className="text-sm font-medium text-gray-900" data-id="element-82">
                      Registrar
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-83">{domain.registrar}</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-84">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" data-id="element-85" />
                  <div data-id="element-86">
                    <p className="text-sm font-medium text-gray-900" data-id="element-87">
                      Important Dates
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-88">
                      Created: {domain.creationDate}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-89">
                      Expires: {domain.expiryDate}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-90">
                      Last Updated: {domain.lastUpdated}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-91">
              <h2 className="text-sm font-medium text-gray-500 mb-4" data-id="element-92">
                WHOIS Record Details
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-93">
                <div className="flex items-start" data-id="element-94">
                  <ServerIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" data-id="element-95" />
                  <div data-id="element-96">
                    <p className="text-sm font-medium text-gray-900" data-id="element-97">
                      Nameservers
                    </p>
                    {domain.nameservers.map((ns, index) => <p key={index} className="text-sm text-gray-500" data-id="element-98">
                        {ns}
                      </p>)}
                  </div>
                </div>
                <div className="flex items-start" data-id="element-99">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" data-id="element-100" />
                  <div data-id="element-101">
                    <p className="text-sm font-medium text-gray-900" data-id="element-102">
                      Registration Details
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-103">
                      WHOIS Privacy:{' '}
                      {domain.whoisPrivacy ? 'Enabled' : 'Disabled'}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-104">
                      Registrant Country: {domain.registrantCountry}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};